import React, { useEffect, useState } from 'react';
import productService from '../services/productService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { ShimmerTable, ShimmerBox } from '../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Products = () => {
  const { token } = useAuth();
  const { t } = useLanguage(); // eslint-disable-line no-unused-vars
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({
    merchant_id: '',
    product_name: '',
    description: '',
    image_url: '',
    original_price: '',
    category: '',
    is_available: true,
    stock_quantity: 0
  });
  const [merchants, setMerchants] = useState([]);
  const [formError, setFormError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    setLoading(true);
    productService.getProducts(token)
      .then(setProducts)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    // Fetch merchants for dropdown
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/merchants`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setMerchants(data.data || []));
  }, [token]);

  const openAddModal = () => {
    setForm({
      merchant_id: '',
      product_name: '',
      description: '',
      image_url: '',
      original_price: '',
      category: '',
      is_available: true,
      stock_quantity: 0
    });
    setEditProduct(null);
    setFormError(null);
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setForm({
      merchant_id: product.merchant_id,
      product_name: product.product_name,
      description: product.description || '',
      image_url: product.image_url || '',
      original_price: product.original_price,
      category: product.category || '',
      is_available: product.is_available,
      stock_quantity: product.stock_quantity
    });
    setEditProduct(product);
    setFormError(null);
    setImageFile(null);
    setImagePreview(product.image_url || null);
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      if (!form.merchant_id || !form.product_name || !form.original_price) {
        setFormError('Merchant, Name, and Price are required');
        return;
      }
      const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';
      if (editProduct) {
        if (imageFile) {
          const fd = new FormData();
          fd.append('merchant_id', form.merchant_id);
          fd.append('product_name', form.product_name);
          fd.append('description', form.description || '');
          fd.append('original_price', form.original_price);
          fd.append('category', form.category || '');
          fd.append('is_available', form.is_available);
          fd.append('stock_quantity', form.stock_quantity || 0);
          fd.append('image', imageFile);
          const res = await fetch(`${API}/api/admin/products/${editProduct.id}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          if (!res.ok) throw new Error('Failed to update product');
        } else {
          await productService.updateProduct(token, editProduct.id, form);
        }
      } else {
        if (imageFile) {
          const fd = new FormData();
          fd.append('merchant_id', form.merchant_id);
          fd.append('product_name', form.product_name);
          fd.append('description', form.description || '');
          fd.append('original_price', form.original_price);
          fd.append('category', form.category || '');
          fd.append('is_available', form.is_available);
          fd.append('stock_quantity', form.stock_quantity || 0);
          fd.append('image', imageFile);
          const res = await fetch(`${API}/api/admin/products`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          if (!res.ok) throw new Error('Failed to add product');
        } else {
          await productService.addProduct(token, form);
        }
      }
      setShowModal(false);
      setLoading(true);
      productService.getProducts(token)
        .then(setProducts)
        .catch(setError)
        .finally(() => setLoading(false));
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    setLoading(true);
    await productService.deleteProduct(token, productId);
    productService.getProducts(token)
      .then(setProducts)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  const filteredProducts = products.filter(p =>
    p.product_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Products"
        actions={
          <Button onClick={openAddModal}>Add Product</Button>
        }
      />

      <Input
        type="text"
        placeholder="Search products..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="max-w-xs"
      />

      {loading ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <ShimmerBox width="w-64" height="h-10" />
            <ShimmerBox width="w-32" height="h-10" />
          </div>
          <ShimmerTable rows={8} columns={8} />
        </div>
      ) : error ? (
        <p className="text-destructive">{error.message || 'Error loading products'}</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Merchant Email</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map(product => (
                  <TableRow key={product.id}>
                    <TableCell>{product.product_name}</TableCell>
                    <TableCell>{product.category}</TableCell>
                    <TableCell>{product.original_price}</TableCell>
                    <TableCell>
                      <Badge variant={product.is_available ? 'success' : 'secondary'}>
                        {product.is_available ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>{product.stock_quantity}</TableCell>
                    <TableCell>{product.merchants?.business_name || '-'}</TableCell>
                    <TableCell>{product.merchants?.email || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => { window.location.href = `/products/${product.id}`; }}
                        >
                          View Details
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(product)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(product.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Merchant</Label>
              <Select
                value={form.merchant_id || undefined}
                onValueChange={(value) => setForm(f => ({ ...f, merchant_id: value }))}
                disabled={!!editProduct}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select merchant..." />
                </SelectTrigger>
                <SelectContent>
                  {merchants.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.business_name} ({m.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="product_name">Name</Label>
              <Input
                id="product_name"
                name="product_name"
                value={form.product_name}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={form.description}
                onChange={handleFormChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product_image">Product Image</Label>
              <Input
                id="product_image"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  setImageFile(f || null);
                  if (f) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setImagePreview(ev.target?.result);
                    reader.readAsDataURL(f);
                  } else {
                    setImagePreview(null);
                  }
                }}
              />
              {imagePreview && (
                <img src={imagePreview} alt="Preview" className="mt-2 h-20 w-20 rounded-md object-cover" />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="image_url">Image URL</Label>
              <Input
                id="image_url"
                name="image_url"
                value={form.image_url}
                onChange={handleFormChange}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                name="category"
                value={form.category}
                onChange={handleFormChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="original_price">Price</Label>
              <Input
                id="original_price"
                name="original_price"
                type="number"
                value={form.original_price}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock_quantity">Stock Quantity</Label>
              <Input
                id="stock_quantity"
                name="stock_quantity"
                type="number"
                value={form.stock_quantity}
                onChange={handleFormChange}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="is_available"
                checked={!!form.is_available}
                onCheckedChange={(checked) => setForm(f => ({ ...f, is_available: checked }))}
              />
              <Label htmlFor="is_available">Available</Label>
            </div>
            <DialogFooter>
              <Button type="submit" className="w-full sm:w-auto">
                {editProduct ? 'Update' : 'Add'} Product
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
